// @oasis-permission-inherits: /jerboa-client-preview (jerboa:view)
import { Link, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";
import {
  formatDurationMs,
  formatFps,
  formatMb,
  formatTimestamp,
  playbackQualityFromP90,
  qualityBadgeClass,
  statusVariant,
} from "~/modules/jerboa-client-preview/formatters";
import { FpsChart } from "~/modules/jerboa-client-preview/fps-chart";
import {
  avgFpsP10,
  hasMeasuredMetric,
  webCannotKeepUpShare,
  webHeadroom,
  webHitchShareMedian,
  webLowFpsShare,
} from "~/modules/jerboa-client-preview/playback-derived";
import {
  fetchJerboaClientPreviewAnalysisQuery,
  fetchJerboaClientPreviewsQuery,
} from "~/modules/jerboa-client-preview/queries";
import {
  jerboaClientPreviewSourcePlatformSchema,
  jerboaClientPreviewStatusSchema,
  type JerboaClientPreviewSourcePlatform,
  type JerboaClientPreviewStatus,
  type PlaybackAnalysisBucket,
  type PlaybackSegmentAggregate,
} from "~/modules/jerboa-client-preview/schemas";
import { SegmentMetricChart } from "~/modules/jerboa-client-preview/segment-metric-chart";
import {
  BucketMetricChart,
  CHART_COLORS,
  type BucketMetricLine,
} from "~/modules/jerboa/components/bucket-metric-chart";
import { ChartPanel } from "~/modules/jerboa/components/chart-panel";
import { JerboaCommitLink } from "~/modules/jerboa/components/user-env-card";

const metricDaysOptions = [1, 7, 30] as const;
type MetricDays = (typeof metricDaysOptions)[number];

const searchSchema = z.object({
  page: z.number().optional(),
  status: jerboaClientPreviewStatusSchema.optional(),
  projectId: z.string().optional(),
  sourcePlatform: jerboaClientPreviewSourcePlatformSchema.optional(),
  metricDays: z.union([z.literal(1), z.literal(7), z.literal(30)]).optional(),
});

export const Route = createFileRoute("/_dash/jerboa-client-preview/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

interface PlaybackMetricsProps {
  isLoading: boolean;
  error: Error | null;
  buckets: PlaybackAnalysisBucket[] | undefined;
  showFpsP10: boolean;
}

const getOsSegments = (bucket: PlaybackAnalysisBucket) => bucket.osSegments;
const getBrowserSegments = (bucket: PlaybackAnalysisBucket) => bucket.browserSegments;
const getGpuSegments = (bucket: PlaybackAnalysisBucket) => bucket.gpuSegments;

const LOW_FPS_LINES: BucketMetricLine<PlaybackAnalysisBucket>[] = [
  {
    key: "lowFps",
    label: "Below 30 fps",
    color: CHART_COLORS[4],
    getValue: webLowFpsShare,
  },
];

// Deliberately not `starvedSessionShare`: that one counts any session with a
// single empty-queue tick (~70% of the sessions it flags starved <=250ms
// total, a play-start warm-up blip) and reads ~55% of web sessions. The
// tiers below separate severity instead (JRB-1820).
const HEADROOM_LINES: BucketMetricLine<PlaybackAnalysisBucket>[] = [
  { key: "headroom", label: "Median headroom", color: CHART_COLORS[1], getValue: webHeadroom },
];

const CANNOT_KEEP_UP_LINES: BucketMetricLine<PlaybackAnalysisBucket>[] = [
  {
    key: "cannotKeepUp",
    label: "Cannot keep up",
    color: CHART_COLORS[4],
    getValue: webCannotKeepUpShare,
  },
];

const HITCH_LINES: BucketMetricLine<PlaybackAnalysisBucket>[] = [
  {
    key: "hitch",
    label: "Median hitch share",
    color: CHART_COLORS[2],
    getValue: webHitchShareMedian,
  },
];

const RENDER_BUSY_MEDIAN = {
  kind: "share" as const,
  getValue: (segment: PlaybackSegmentAggregate) =>
    (segment.renderBusyCount ?? 0) > 0 ? segment.renderBusyShareMedian : null,
};

const STARVED_LINES: BucketMetricLine<PlaybackAnalysisBucket>[] = [
  {
    key: "over1pct",
    label: "Over 1% of playing time",
    color: CHART_COLORS[0],
    getValue: (bucket) => bucket.starvedOver1PctShare,
  },
  {
    key: "over5pct",
    label: "Over 5%",
    color: CHART_COLORS[2],
    getValue: (bucket) => bucket.starvedOver5PctShare,
  },
  {
    key: "over10pct",
    label: "Over 10%",
    color: CHART_COLORS[4],
    getValue: (bucket) => bucket.starvedOver10PctShare,
  },
];

const FRAME_GAP_LINES: BucketMetricLine<PlaybackAnalysisBucket>[] = [
  {
    key: "median",
    label: "Median",
    color: CHART_COLORS[0],
    getValue: (bucket) => bucket.longestFrameGapMsMedian,
  },
  {
    key: "p90",
    label: "P90",
    color: CHART_COLORS[3],
    getValue: (bucket) => bucket.longestFrameGapMsP90,
  },
];

function PlaybackMetrics({ isLoading, error, buckets, showFpsP10 }: PlaybackMetricsProps) {
  if (isLoading) {
    return <Skeleton className="h-[200px] w-full rounded-md" />;
  }

  if (error) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-md border border-destructive bg-destructive/10 p-4 text-center text-sm text-destructive">
        <div>
          <p className="font-medium">Failed to load playback metrics</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      </div>
    );
  }

  if (!buckets || buckets.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No playback metrics found for this period
      </div>
    );
  }

  return (
    <>
      <FpsChart buckets={buckets} showP10={showFpsP10} />
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartPanel title="Median FPS by OS">
          <SegmentMetricChart buckets={buckets} getSegments={getOsSegments} />
        </ChartPanel>
        <ChartPanel title="Median FPS by browser">
          <SegmentMetricChart buckets={buckets} getSegments={getBrowserSegments} />
        </ChartPanel>
        <ChartPanel title="Median FPS by GPU family" note="Only sessions that reported a GPU.">
          <SegmentMetricChart buckets={buckets} getSegments={getGpuSegments} connectNulls={false} />
        </ChartPanel>
        <ChartPanel
          title="Sessions below 30 fps"
          note="An outcome measure across all causes — decode starvation, render load, or content that simply plays below 30 fps."
        >
          <BucketMetricChart buckets={buckets} kind="share" lines={LOW_FPS_LINES} />
        </ChartPanel>
        <ChartPanel
          title="Playback headroom"
          note="1 minus the median session's binding-stage utilization — the larger of CPU (scene build + render) and GPU busy shares. High means the engine is coasting; near zero means little capacity remains for workload variance."
        >
          <BucketMetricChart buckets={buckets} kind="share" lines={HEADROOM_LINES} />
        </ChartPanel>
        <ChartPanel
          title="Sessions that cannot keep up"
          note="Sessions materially starved (over 5% of playing time) or with the binding stage near saturation (over 90% busy). Saturation is a proxy, not proof of dropped frames — a session can deliver at 93% busy — but it leaves no headroom for variance; sub-saturation load lives in the headroom and render-busy panels."
        >
          <BucketMetricChart buckets={buckets} kind="share" lines={CANNOT_KEEP_UP_LINES} />
        </ChartPanel>
        <ChartPanel
          title="Sessions starved on decode"
          note="Share of sessions that spent more than 1%, 5%, or 10% of their playing time waiting on the video decoder."
        >
          <BucketMetricChart buckets={buckets} kind="share" lines={STARVED_LINES} />
        </ChartPanel>
        <ChartPanel
          title="Render busy share by browser"
          note="Median share of playing time the render call held the main thread. On Chrome the GPU works asynchronously and is not counted, so low values there are expected; Safari's blocking submit makes this its render-load signal."
        >
          <SegmentMetricChart
            buckets={buckets}
            getSegments={getBrowserSegments}
            metric={RENDER_BUSY_MEDIAN}
          />
        </ChartPanel>
        <ChartPanel
          title="Frame pacing breaks"
          note="The median session's share of frames whose gap broke the session's own pacing — the how-often companion to the worst-gap panel."
        >
          <BucketMetricChart buckets={buckets} kind="share" lines={HITCH_LINES} />
        </ChartPanel>
        <ChartPanel
          title="Worst frame gap per play cycle"
          note="Worst gap measured per play cycle, not per editor session."
        >
          <BucketMetricChart buckets={buckets} kind="ms" lines={FRAME_GAP_LINES} />
        </ChartPanel>
      </div>
    </>
  );
}

function RouteComponent() {
  const { page, status, projectId, sourcePlatform, metricDays } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const selectedMetricDays = metricDays ?? 7;

  const params = useMemo(
    () => ({ status, projectId, sourcePlatform }),
    [status, projectId, sourcePlatform]
  );
  const searchParams = useMemo(
    () => ({ status, projectId, sourcePlatform, metricDays }),
    [status, projectId, sourcePlatform, metricDays]
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaClientPreviewsQuery.useInfiniteQuery({
      params,
    });

  const {
    data: analysisData,
    isLoading: isAnalysisLoading,
    error: analysisError,
  } = fetchJerboaClientPreviewAnalysisQuery.useQuery({
    params: { days: selectedMetricDays },
  });

  const analysisBuckets = analysisData?.analysis.buckets;
  // The RUM aggregate API has no pc10, so the p10 line is dropped for windows
  // that only contain RUM-sourced buckets. Decided once here: the chart plots
  // it and the subtitle names what is actually plotted.
  const showFpsP10 = hasMeasuredMetric(analysisBuckets ?? [], avgFpsP10);
  const fpsSeriesLabel = showFpsP10 ? "Mean / p10 / p90" : "Mean / p90";

  const {
    currentPageIndex,
    currentPageData,
    totalPages,
    onPreviousPage,
    onNextPage,
    onPageChange,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => navigate({ search: { ...searchParams, page: nextPage } }),
  });

  const onStatusChange = (nextStatus: JerboaClientPreviewStatus | undefined) => {
    navigate({
      search: { page: 1, projectId, sourcePlatform, metricDays, status: nextStatus },
    });
  };

  const onSourcePlatformChange = (
    nextSourcePlatform: JerboaClientPreviewSourcePlatform | undefined
  ) => {
    navigate({
      search: { page: 1, projectId, status, metricDays, sourcePlatform: nextSourcePlatform },
    });
  };

  const onMetricDaysChange = (nextMetricDays: MetricDays) => {
    navigate({ search: { ...params, page, metricDays: nextMetricDays } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Client Playback</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <div className="rounded-md border bg-muted/20 p-4">
        <div className="mb-3 border-b pb-3">
          <h2 className="text-base font-semibold">Playback analysis — web only (Datadog RUM)</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Aggregated from Datadog RUM scoped to service:mirage-studio-web. iOS sessions are
            excluded, as are play cycles shorter than 3 seconds. The platform filter below narrows
            only the session list, not this section.
          </p>
        </div>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">
              Avg playback FPS over the last {selectedMetricDays}{" "}
              {selectedMetricDays === 1 ? "day" : "days"}
            </p>
            <p className="text-xs text-muted-foreground">{fpsSeriesLabel} — 2-hour buckets</p>
          </div>
          <Tabs
            value={String(selectedMetricDays)}
            onValueChange={(value) => onMetricDaysChange(Number(value) as MetricDays)}
          >
            <TabsList className="h-8">
              {metricDaysOptions.map((days) => (
                <TabsTrigger key={days} value={String(days)} className="text-xs">
                  {days}d
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <PlaybackMetrics
          isLoading={isAnalysisLoading}
          error={analysisError}
          buckets={analysisBuckets}
          showFpsP10={showFpsP10}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Status:</span>
        {(["success", "error"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onStatusChange(status === s ? undefined : s)}
            className="inline-flex"
          >
            <Badge variant={status === s ? statusVariant(s) : "outline"}>{s}</Badge>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Platform:</span>
        {(["web", "ios"] as const).map((platform) => (
          <button
            key={platform}
            type="button"
            onClick={() =>
              onSourcePlatformChange(sourcePlatform === platform ? undefined : platform)
            }
            className="inline-flex"
          >
            <Badge variant={sourcePlatform === platform ? "default" : "outline"}>{platform}</Badge>
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load playback results</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Project ID</TableHead>
              <TableHead>Dimensions</TableHead>
              <TableHead>Frames</TableHead>
              <TableHead>FPS (P90)</TableHead>
              <TableHead>Session</TableHead>
              <TableHead>Init</TableHead>
              <TableHead>Peak Memory</TableHead>
              <TableHead>Peak JS Heap</TableHead>
              <TableHead>App</TableHead>
              <TableHead>Jerboa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageData.page.map((result) => {
              const quality = playbackQualityFromP90(result.p90PlaybackFps, result.targetFps);
              return (
                <TableRow
                  key={result.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    router.navigate({
                      to: "/jerboa-client-preview/$id",
                      params: { id: result.id },
                      search: { page: currentPageIndex + 1, ...searchParams },
                    })
                  }
                >
                  <TableCell>{formatTimestamp(result.createdAt)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{result.sourcePlatform}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(result.status)}>{result.status}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {result.captionsProjectId ? (
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: result.captionsProjectId }}
                        className="text-blue-600 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {result.captionsProjectId}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {result.width}×{result.height}
                  </TableCell>
                  <TableCell>{result.framesRendered}</TableCell>
                  <TableCell>
                    <div className="flex items-baseline gap-1">
                      <Badge
                        variant="outline"
                        className={cn("font-mono", qualityBadgeClass(quality))}
                      >
                        {formatFps(result.p90PlaybackFps)}
                      </Badge>
                      {result.targetFps != null && (
                        <span className="text-xs text-muted-foreground">/ {result.targetFps}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatDurationMs(result.sessionDurationMs)}</TableCell>
                  <TableCell>{formatDurationMs(result.initDurationMs)}</TableCell>
                  <TableCell>
                    {formatMb(
                      result.sourcePlatform === "ios"
                        ? result.peakNativePhysicalFootprintMb
                        : result.peakWasmMemoryMb
                    )}
                  </TableCell>
                  <TableCell>{formatMb(result.peakJsHeapMb)}</TableCell>
                  <TableCell className="font-mono text-xs">{result.appVersion ?? "-"}</TableCell>
                  <TableCell className="font-mono text-xs" onClick={(e) => e.stopPropagation()}>
                    {result.jerboaVersion ? <JerboaCommitLink sha={result.jerboaVersion} /> : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {currentPageData.page.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} className="text-center text-muted-foreground">
                  No playback results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
